import { ApexOptions } from 'apexcharts';
import { motion } from 'framer-motion';
import { memo, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { NoRecordFound } from '@components';
import { LINE_GRAPH_RESPONSIVE_CONFIG } from '@constants';
import { DashboardEndpoint } from '@enums';
import { useChartDimensions, useTimeAndDomainFilters, useFormList } from '@hooks';

import { convertGraphDateTimeFormat, formatYAxisValue, validateValueEqual } from '@utils';

export const CdnConfigurationDashboardTotalTrafficGraph = memo(({ payload }: any) => {
  const graphData = useFormList(payload);

  const { timeFilter } = useTimeAndDomainFilters({
    formType: payload.formType,
    endpoint: DashboardEndpoint.TOTAL_TRAFFIC,
  });

  const seriesData = useMemo(
    () =>
      (graphData ?? []).map(({ timestamp = 0, total_egress = 0 }) => ({
        x: timestamp * 1000,
        y: total_egress,
      })),
    [graphData],
  );

  const { chartHeight } = useChartDimensions({ dimensions: LINE_GRAPH_RESPONSIVE_CONFIG });

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        zoom: { enabled: false },
        stroke: { curve: 'smooth' },
        toolbar: { show: false },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: { enabled: true, delay: 150 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          inverseColors: false,
          opacityFrom: 0.7,
          opacityTo: 0,
          stops: [0, 90, 100],
        },
      },
      dataLabels: { enabled: false },
      grid: {
        padding: { top: 10, right: 0, bottom: 0, left: 15 },
      },
      xaxis: {
        type: 'datetime',
        lines: { show: false },
        labels: {
          format: convertGraphDateTimeFormat(timeFilter),
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
        labels: {
          formatter: (value) =>
            validateValueEqual(value, 0) ? '' : formatYAxisValue({ value, useUnit: true, allowFloating: true }),
        },
      },
      tooltip: {
        x: { format: 'dd MMM yyyy HH:mm' },
        y: {
          formatter: (value) => formatYAxisValue({ value, useUnit: true, allowFloating: true }),
        },
      },
    }),
    [timeFilter, chartHeight],
  );

  const series = useMemo(() => [{ name: 'Total Traffic', data: seriesData }], [seriesData]);

  if (!timeFilter) return null;
  if (!seriesData.length) {
    return <NoRecordFound />;
  }
  return (
    <motion.div
      className="flex justify-center -mx-2 mt-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <ReactApexChart options={options} series={series} type="area" height={chartHeight} className="w-full" />
    </motion.div>
  );
});
